import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartGroupDto } from '@data/_generated-api/platform-api/models/platform-chart-group-dto';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** Chart + voorgeselecteerde groepen; null = dialog gesloten. */
export interface HdChartSetGroupsTarget {
  chartId: string;
  chartName: string;
  selectedGroupIds: string[];
}

export interface HdChartSetGroupsResult {
  chartId: string;
  groupIds: string[];
}

@Component({
  selector: 'hd-chart-set-groups-dialog',
  standalone: true,
  imports: [SharedModule, FormsModule, NgSelectModule],
  templateUrl: './hd-chart-set-groups-dialog.component.html',
  styleUrl: './hd-chart-set-groups-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartSetGroupsDialogComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly target = model<HdChartSetGroupsTarget | null>(null);
  readonly groups = input<PlatformChartGroupDto[]>([]);
  readonly saved = output<HdChartSetGroupsResult>();
  /**
   * "Nieuwe groep": de host opent de groep-toevoegen-dialog (bovenop deze) en
   * zet na het aanmaken de nieuwe groep mee in de selectie. Draagt de huidige,
   * nog niet opgeslagen selectie mee zodat die niet verloren gaat.
   */
  readonly createGroupRequested = output<string[]>();

  protected readonly selectedIds = signal<string[]>([]);

  constructor() {
    effect(() => {
      const target = this.target();
      if (target) this.selectedIds.set([...target.selectedGroupIds]);
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chartManager.groups.setGroups'),
      placeholder: translate('humanDesign.chartManager.groups.setGroupsPlaceholder'),
      save: translate('humanDesign.chartManager.groups.save'),
      cancel: translate('humanDesign.chartManager.groups.cancel'),
      newGroup: translate('humanDesign.chartManager.groups.newGroup'),
    };
  });

  /** Alle groepen, plat met inspring-prefix (zelfde weergave als de parent-select). */
  protected readonly groupOptions = computed(() => {
    const groups = this.groups();
    const childrenByParent = new Map<string | null, PlatformChartGroupDto[]>();
    for (const group of groups) {
      const key = group.parentGroupId ?? null;
      const siblings = childrenByParent.get(key) ?? [];
      siblings.push(group);
      childrenByParent.set(key, siblings);
    }

    const options: { value: string; label: string }[] = [];
    const visit = (parentId: string | null, level: number) => {
      for (const group of childrenByParent.get(parentId) ?? []) {
        if (!group.id) continue;
        options.push({ value: group.id, label: `${'—'.repeat(level)}${level ? ' ' : ''}${group.name}` });
        visit(group.id, level + 1);
      }
    };
    visit(null, 0);
    return options;
  });

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected requestCreateGroup(): void {
    this.createGroupRequested.emit([...this.selectedIds()]);
  }

  protected save(): void {
    const target = this.target();
    if (!target) return;
    this.saved.emit({ chartId: target.chartId, groupIds: [...this.selectedIds()] });
    this.target.set(null);
  }
}
